import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import type {
  PremiseConfig,
  SchemeBinding,
  SchemeContent,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { isCarePremise } from "@shared/types/foyer";
import { hasPromotionsLicense } from "@shared/products";
import { certifiedEnabledIds } from "@shared/app-merge";
import {
  certificationOf,
  CERTIFICATION_LABEL,
} from "@shared/app-certification";
import type { TemplateSubSection } from "../PremiseSidebar";
import SchemePicker from "./SchemePicker";
import DesignTokenEditor from "./DesignTokenEditor";
import TypographyEditor from "./TypographyEditor";
import ScreensEditor from "./ScreensEditor";
import ScreenStateBar from "./ScreenStateBar";
import {
  BrandingEditor,
  BackgroundEditor,
  AppsEditor,
  StationsEditor,
  InfoEditor,
  WifiEditor,
  WelcomeEditor,
  RelativesEditor,
  InstagramEditor,
  WeatherEditor,
  HomeCardsEditor,
  SlotsEditor,
} from "./ContentEditor";
import CustomScreenEditor from "./CustomScreenEditor";
import MenuEditor from "./MenuEditor";
import VilmerEditor from "./VilmerEditor";
import { foyerApi, type AppRecord } from "../../http/foyer";

/**
 * The global app catalogue and what it pins, for one premise.
 *
 * Fetched ONCE per page and shared by the Apps pane and the screen list. They
 * ask the same question of it - which certified apps are switched on (NFLX-4) -
 * and a second fetch is a second answer that can be a beat behind the first.
 */
export function useTemplateApps(premise: PremiseConfig) {
  const [apps, setApps] = useState<AppRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listApps()
      .then((list) => alive && setApps(Array.isArray(list) ? list : []))
      .catch(() => alive && setApps([]));
    return () => {
      alive = false;
    };
  }, []);

  // A certified app that is ON pins part of the arrangement: Netflix must be
  // reachable, so the Apps screen stops being switchable while it is showing.
  // NFLX-6: a care premise offers no certified app at all. Read from the DRAFT,
  // not the saved config, so a change takes effect before Save.
  const isCare = isCarePremise({
    type: premise.type,
    segment: premise.segment,
  });
  const certifiedOn = certifiedEnabledIds(
    apps,
    premise.scheme?.content.disabledAppIds,
    isCare
  );
  const certNote = certifiedOn.size
    ? `Required while ${[
        ...new Set(
          (apps ?? [])
            .filter((a) => certifiedOn.has(a.id))
            .map((a) => CERTIFICATION_LABEL[certificationOf(a)!])
        ),
      ].join(" / ")} is switched on in Apps`
    : undefined;
  return { apps, isCare, certifiedOn, certNote };
}

export type TemplateApps = ReturnType<typeof useTemplateApps>;

interface Props {
  premise: PremiseConfig;
  sub: TemplateSubSection;
  onChange: (next: PremiseConfig) => void;
  /** From {@link useTemplateApps}, owned by the page. */
  apps: TemplateApps;
  /** Move to another sub-section - lets the Screens board link into each editor. */
  onSelectSub?: (sub: TemplateSubSection) => void;
  /**
   * Show the screen's own on/off bar above its editor. The Start & navigation
   * pages state on/off in their own "Active" control, so they turn this off.
   */
  stateBar?: boolean;
}

/**
 * The controls for ONE template sub-section, without any page chrome.
 *
 * Shared by the old single-column `TemplateEditor` (still used for Promotions'
 * home-screen slot) and the Start & navigation pages, which place the same
 * editors in a list / editor / preview layout. One switch, so an editor added
 * here reaches both.
 */
export default function TemplateSubEditor({
  premise,
  sub,
  onChange,
  apps: { apps, isCare, certifiedOn, certNote },
  onSelectSub,
  stateBar = true,
}: Props) {
  const binding = premise.scheme;
  const setBinding = (next: SchemeBinding) =>
    onChange({ ...premise, scheme: next });
  const setContent = (content: SchemeContent) => {
    if (binding) setBinding({ ...binding, content });
  };
  const manifest = binding ? SCHEME_MANIFESTS[binding.key] : undefined;
  const loc = {
    supportedLocales: premise.supportedLocales,
    defaultLocale: premise.defaultLocale,
  };

  if (sub === "picker") {
    return <SchemePicker premise={premise} onChange={onChange} />;
  }

  if (!binding) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pick a template first - choose one under{" "}
        <strong>Appearance → Pick template</strong>.
      </Typography>
    );
  }

  return (
    <>
      {manifest && sub === "design" && (
        <DesignTokenEditor
          manifest={manifest}
          value={binding.tokens}
          onChange={(tokens) => setBinding({ ...binding, tokens })}
        />
      )}
      {manifest && sub === "typography" && (
        <TypographyEditor
          manifest={manifest}
          value={binding.tokens}
          onChange={(tokens) => setBinding({ ...binding, tokens })}
        />
      )}
      {manifest && sub === "knobs" && (
        <ScreensEditor
          manifest={manifest}
          value={binding.knobs}
          content={binding.content}
          onChange={(knobs) => setBinding({ ...binding, knobs })}
          onOpenSub={onSelectSub}
          hasPromotions={hasPromotionsLicense(premise.products)}
          appsCertified={certifiedOn.size > 0}
          certificationNote={certNote}
        />
      )}

      {/* Every screen editor states its own on/off, so you never have to leave
          the page you are filling in to discover it isn't being shown.
          Promotions is the exception: its switch is `showPromotions` on the
          Screens board, and repeating it here read as a second, per-promotion
          on/off. One switch for the block, in one place. */}
      {stateBar && sub !== "knobs" && sub !== "slots" && (
        <ScreenStateBar
          sub={sub}
          manifest={manifest}
          value={binding.knobs}
          onChange={(knobs) => setBinding({ ...binding, knobs })}
        />
      )}
      {sub === "branding" && (
        <BrandingEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {manifest && sub === "background" && (
        <BackgroundEditor
          content={binding.content}
          manifest={manifest}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "apps" && (
        <AppsEditor
          content={binding.content}
          {...loc}
          global={apps}
          isCare={isCare}
          onChange={setContent}
        />
      )}
      {sub === "radio" && (
        <StationsEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "info" && (
        <InfoEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {sub === "menu" && (
        <MenuEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {sub === "vilmer" && (
        <VilmerEditor
          content={binding.content}
          rooms={premise.rooms ?? []}
          onChange={setContent}
        />
      )}
      {manifest && sub === "slots" && (
        <SlotsEditor
          content={binding.content}
          manifest={manifest}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "wifi" && (
        <WifiEditor content={binding.content} {...loc} onChange={setContent} />
      )}
      {sub === "welcome" && (
        <WelcomeEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "relatives" && (
        <RelativesEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "instagram" && (
        <InstagramEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {sub === "weather" && (
        <WeatherEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
      {manifest && sub === "custom" && (
        <CustomScreenEditor
          manifest={manifest}
          knobs={binding.knobs}
          onKnobs={(knobs) => setBinding({ ...binding, knobs })}
          content={binding.content}
          onChange={setContent}
          {...loc}
        />
      )}
      {sub === "icons" && (
        <HomeCardsEditor
          content={binding.content}
          {...loc}
          onChange={setContent}
        />
      )}
    </>
  );
}
